import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';
import { CHAT_MEDIA_ASPECT_RATIO } from '../../constants/chatMedia.constants';

export const CHAT_MEDIA_MAX_WIDTH = 'min(320px, 76%)';
export { CHAT_MEDIA_ASPECT_RATIO } from '../../constants/chatMedia.constants';
export const CHAT_MEDIA_RADIUS = '18px';

const mediaShimmer = keyframes`
	0% {
		transform: translateX(-120%);
	}

	100% {
		transform: translateX(120%);
	}
`;

const mediaPulse = keyframes`
	0%,
	100% {
		opacity: 0.45;
	}

	50% {
		opacity: 0.9;
	}
`;

export const ChatMediaMessageWrap = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
	max-width: ${CHAT_MEDIA_MAX_WIDTH};

	@media (max-width: ${THEME.mobileBreakpoint}) {
		max-width: min(260px, 88%);
	}
`;

export const ChatMediaFrame = styled.div`
	position: relative;
	width: 100%;
	aspect-ratio: ${CHAT_MEDIA_ASPECT_RATIO};
	border-radius: ${CHAT_MEDIA_RADIUS};
	overflow: hidden;
	background: transparent;
	box-shadow:
		0 16px 42px rgba(0, 0, 0, 0.38),
		0 0 28px rgba(255, 77, 242, 0.1);
	isolation: isolate;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		border-radius: 14px;
		box-shadow: 0 10px 24px rgba(0, 0, 0, 0.32);
	}
`;

export const ChatMediaSkeleton = styled.div`
	position: absolute;
	inset: 0;
	z-index: 2;
`;

export const ChatMediaOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 2;
	display: grid;
	place-content: center;
	justify-items: center;
	gap: 12px;
	padding: 20px;
	color: rgba(255, 245, 255, 0.92);
	pointer-events: none;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(8, 3, 12, 0.12) 0%,
			rgba(8, 3, 12, 0.42) 100%
		);
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			105deg,
			transparent 42%,
			rgba(255, 255, 255, 0.12) 50%,
			transparent 58%
		);
		transform: translateX(-120%);
		animation: ${mediaShimmer} 2.4s ease-in-out infinite;
	}
`;

export const ChatMediaSpinner = styled.div`
	position: relative;
	z-index: 1;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.12);
	border-top-color: ${THEME.pink};
	border-right-color: rgba(255, 211, 107, 0.72);
	animation: spin 0.85s linear infinite;

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

export const ChatMediaOverlayCopy = styled.div`
	position: relative;
	z-index: 1;
	display: grid;
	gap: 6px;
	text-align: center;
`;

export const ChatMediaOverlayTitle = styled.span`
	font-size: 0.92rem;
	font-weight: 700;
	color: ${THEME.text};
`;

export const ChatMediaOverlayMeta = styled.span`
	font-size: 0.76rem;
	font-weight: 600;
	color: rgba(216, 189, 228, 0.82);
	animation: ${mediaPulse} 2s ease-in-out infinite;
`;

export const ChatMediaProgressTrack = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 3;
	height: 6px;
	background: rgba(255, 255, 255, 0.08);
`;

const chatMediaCountdown = keyframes`
	from {
		transform: scaleX(1);
	}

	to {
		transform: scaleX(0);
	}
`;

export const ChatMediaProgressBar = styled.div`
	height: 100%;
	width: 100%;
	background: ${THEME.gradientPrimary};
	transform-origin: left;
	animation: ${chatMediaCountdown} ${({ $duration }) => `${$duration}s`} linear forwards;
`;

export const ChatMediaImage = styled.img`
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
	object-position: center;
	opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
	transition: opacity 240ms ease;
`;

export const ChatVideoShell = styled.div`
	position: absolute;
	inset: 0;
	cursor: pointer;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 2;
		pointer-events: none;
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.05) 0%,
			transparent 18%,
			transparent 72%,
			rgba(8, 3, 12, 0.42) 100%
		);
		transition: opacity 280ms ease;
		opacity: ${({ $isPlaying }) => ($isPlaying ? 0.55 : 1)};
	}

	&::after {
		content: '';
		position: absolute;
		inset: -1px;
		z-index: 0;
		pointer-events: none;
		border-radius: inherit;
		opacity: ${({ $isPlaying }) => ($isPlaying ? 1 : 0)};
		box-shadow:
			inset 0 0 0 1px rgba(255, 77, 242, 0.28),
			0 0 32px rgba(255, 77, 242, 0.18);
		transition: opacity 320ms ease;
	}
`;

export const ChatVideoElement = styled.video`
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
	object-position: center;
	background: transparent;
	opacity: ${({ $isReady }) => ($isReady ? 1 : 0)};
	transition: opacity 260ms ease;
`;

const playHintPulse = keyframes`
	0%,
	100% {
		transform: scale(1);
		box-shadow: 0 0 24px rgba(255, 77, 242, 0.22);
	}

	50% {
		transform: scale(1.04);
		box-shadow: 0 0 34px rgba(255, 77, 242, 0.34);
	}
`;

export const ChatVideoPlayHint = styled.div`
	position: absolute;
	inset: 0;
	z-index: 3;
	display: grid;
	place-items: center;
	pointer-events: none;
	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	transition: opacity 220ms ease;

	span {
		width: 58px;
		height: 58px;
		border-radius: 50%;
		display: grid;
		place-items: center;
		border: 1px solid rgba(255, 214, 255, 0.34);
		background: rgba(12, 5, 18, 0.58);
		backdrop-filter: blur(10px);
		color: #fff;
		font-size: 1.1rem;
		padding-left: 3px;
		animation: ${playHintPulse} 2.2s ease-in-out infinite;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		span {
			width: 46px;
			height: 46px;
			font-size: 0.95rem;
		}
	}
`;
